import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { styled } from '@prose-ui/legacy';

import { BeforeAndAfterSection } from 'Apps/LandingPages/Scenes/Skincare/BeforeAndAfterSection';

import { ExpertsModule } from 'Components/ExpertsModule';
import { MetaData } from 'Components/MetaData';
import { SampleFormulationCardCarousel } from '../Components/SampleFormulationCardCarousel';

import { productsCategories, productsSubCategories } from 'constants/products';

import { Faq } from 'Blocks/Faq';
import { HowItWorks } from 'Blocks/HowItWorks';
import { NoListBlock } from 'Blocks/NoListBlock';
import { Numbers } from 'Blocks/Numbers';
import { ProductsCarousel } from 'Blocks/ProductsCarousel';
import { ProductPageHero } from '../Blocks/ProductPageHero';

import ScrollTracker from 'Containers/ScrollTracker';

import { srcOrNextImageProp } from 'PropTypes/imageProps';
import productProps from 'PropTypes/productProps';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import { productsMetadata } from 'assets/metadata/productsMetadata';
import productsStucturedData from 'assets/metadata/productsStructuredData';

import {
  getFilteredFetchedSkincareProductsIsLoaded,
  getSkincareProductsPageContent,
} from 'dux/products/selectors';
import { useTrackProductViewed } from 'dux/products/hooks';

const prepareMetadataProduct = productSlug => productsMetadata[productSlug] ?? {};
const prepareMetadataStructuredDataProduct = productSlug =>
  productsStucturedData[productSlug] ?? {};

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductDetails = ({ content, filteredFetchedProductsIsLoaded, productSlug }) => {
  const {
    heroModule,
    sampleFormulationModule,
    faq,
    productsModule,
    numbersModule,
    noListModule,
    beforeAndAfter,
    expertsModule,
  } = content;

  useTrackProductViewed({
    category: productsCategories.SKINCARE,
    subcategory: productsSubCategories.TOPICALS,
    product: heroModule.product,
  });
  return (
    <>
      <MetaData
        {...prepareMetadataProduct(productSlug)}
        structuredData={prepareMetadataStructuredDataProduct(productSlug)}
      />

      <BaseScene display="block" footer FooterProps={{ hideCTA: true }} header>
        <ScrollTracker>
          <FlexWrapper>
            <ProductPageHero content={heroModule} />
            <Numbers
              backgroundColor="darkerBeige"
              disclaimer={numbersModule.disclaimer}
              disclaimers={numbersModule.disclaimers}
              hasBorder
              LabelProps={{ color: 'vert', variant: 'p1' }}
              NumberProps={{ color: 'vert', variant: 'h2' }}
              numbers={numbersModule.numbers}
              title={numbersModule.title}
              TitleProps={{
                color: 'vert',
                marginBottom: 40,
                variantDesktop: 'h2',
                variantMobile: 'h3',
              }}
              variant="3up"
            />
            <BeforeAndAfterSection content={beforeAndAfter} />
            <SampleFormulationCardCarousel
              cards={sampleFormulationModule.cards}
              subTitle={sampleFormulationModule.subTitle}
              title={sampleFormulationModule.title}
            />
            <NoListBlock content={noListModule} />
            <ExpertsModule content={expertsModule} />
            <HowItWorks cta dataFrom="how-it-works-module" isSkincareOnly />
            <Faq
              backgroundColor="beige"
              dataTestId="skincare-pdp-faq"
              markupName="h3"
              questions={faq.questions}
              title={faq.title}
              variant="light"
            />
            {filteredFetchedProductsIsLoaded && (
              <ProductsCarousel background="veryLightPink" content={productsModule} />
            )}
          </FlexWrapper>
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

ProductDetails.propTypes = {
  filteredFetchedProductsIsLoaded: PropTypes.bool.isRequired,
  content: PropTypes.shape({
    beforeAndAfter: PropTypes.shape({}),
    expertsModule: PropTypes.shape({}),
    sampleFormulationModule: PropTypes.shape({
      subTitle: PropTypes.string,
      title: PropTypes.string,
      cards: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          description: PropTypes.node,
          concerns: PropTypes.string,
          environment: PropTypes.string,
          image: PropTypes.shape({ src: srcOrNextImageProp, alt: PropTypes.string }),
        })
      ),
    }),
    numbersModule: PropTypes.shape({
      disclaimers: PropTypes.arrayOf(PropTypes.string),
      disclaimer: PropTypes.string,
      title: PropTypes.string,
      numbers: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, number: PropTypes.string })
      ),
    }).isRequired,
    heroModule: PropTypes.shape({
      product: productProps.isRequired,
    }).isRequired,
    metrics: PropTypes.shape({
      values: PropTypes.arrayOf(PropTypes.shape({})),
      video: PropTypes.shape({
        videoDesktop: PropTypes.string.isRequired,
        videoMobile: PropTypes.string.isRequired,
      }),
    }).isRequired,
    noListModule: PropTypes.shape({
      cta: PropTypes.shape({
        label: PropTypes.string,
        link: PropTypes.string,
      }).isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        imageDesktop: srcOrNextImageProp.isRequired,
        imageMobile: srcOrNextImageProp.isRequired,
      }),
      list: PropTypes.arrayOf(PropTypes.shape({})),
      title: PropTypes.string.isRequired,
    }),
    productsModule: PropTypes.shape({
      backgroundColor: PropTypes.string,
    }),
    faq: PropTypes.shape({
      questions: PropTypes.arrayOf(PropTypes.shape({})),
      title: PropTypes.string,
    }).isRequired,
  }).isRequired,
  productSlug: PropTypes.string.isRequired,
};

const ConnectedSkincareProductDetails = connect(
  (state, { productSlug }) => ({
    filteredFetchedProductsIsLoaded: getFilteredFetchedSkincareProductsIsLoaded(state),
    content: getSkincareProductsPageContent(state, { productSlug }),
  }),
  {}
)(ProductDetails);

export default ConnectedSkincareProductDetails;
